import { CheckOutlined, InfoCircleOutlined, PlusCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { PageHeader } from '@ant-design/pro-components';
import { useQuery } from '@apollo/client/react';
import { css } from '@emotion/css';
import { Markable } from '@medcom/shared-ui/components/Markable.tsx';
import { monoFont } from '@medcom/shared-ui/styles/globalCss.ts';
import { filterByTokens } from '@medcom/shared-ui/utils/tokenSearch.ts';
import { Button, Flex, Input, Popconfirm, Table, Tooltip, theme } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type React from 'react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDebounce } from 'use-debounce';
import { MainContent } from '../components/MainContent';
import type { DoctorSearchQuery } from '../graphql/generated/graphql.ts';
import { useCurrentContextStore } from '../hooks/store/useCurrentContextStore.ts';
import { tableHoverPointer } from '../styles/globalCss';
import { DOCTOR_SEARCH_QUERY } from './DoctorSearch.gql';

type Doctor = NonNullable<DoctorSearchQuery['doctorsForCurrentUser']>[number];

export const DoctorSearch: React.FC = () => {
  const [search, setSearch] = useState<string | null>(null);
  const [debouncedSearch] = useDebounce(search, 250);
  const { token } = theme.useToken();
  const { setCurrentDoctorId } = useCurrentContextStore();
  const navigate = useNavigate();

  const { data, loading } = useQuery(DOCTOR_SEARCH_QUERY, {
    pollInterval: 60000,
  });

  const filteredDoctors = useMemo(
    () =>
      filterByTokens(data?.doctorsForCurrentUser ?? [], debouncedSearch, [
        d => d.name,
        d => d.meAddress,
        d => d.city,
        d => d.zip,
        d => d.firstName,
        d => d.lastName,
        d => d.assignedLabs.map(l => l.customerId),
      ]),
    [debouncedSearch, data]
  );

  const columns: ColumnsType<Doctor> = [
    {
      title: 'Anzeigename',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      defaultSortOrder: 'ascend',
      width: 130,
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: value => <Markable tokens={debouncedSearch ?? ''}>{value}</Markable>,
    },
    {
      title: 'Aktiv',
      dataIndex: 'disabled',
      key: 'disabled',
      width: 70,
      sorter: (a, b) => Number(b.disabled) - Number(a.disabled),
      render: value => (!value ? <CheckOutlined /> : ''),
    },
    {
      title: 'ME-Adresse',
      dataIndex: 'meAddress',
      key: 'meAddress',
      ellipsis: true,
      width: 120,
      sorter: (a, b) => a.meAddress.localeCompare(b.meAddress),
      render: value => (
        <span className={monoFont}>
          <Markable tokens={debouncedSearch ?? ''}>{value}</Markable>
        </span>
      ),
    },
    {
      title: 'Kd.Nr.',
      dataIndex: 'assignedLabs',
      key: 'assignedLabs',
      width: 90,
      render: (_, record) => (
        <span className={monoFont}>
          <Markable tokens={debouncedSearch ?? ''}>
            {record.assignedLabs
              .filter(it => it.customerId)
              .map(v => v.customerId)
              .join(', ')}
          </Markable>
        </span>
      ),
      ellipsis: true,
    },
    {
      title: 'Vorname',
      dataIndex: 'firstName',
      key: 'firstName',
      ellipsis: true,
      width: 100,
      sorter: (a, b) => a.firstName.localeCompare(b.firstName),
      render: value => <Markable tokens={debouncedSearch ?? ''}>{value}</Markable>,
    },
    {
      title: 'Nachname / Firma',
      dataIndex: 'lastName',
      key: 'lastName',
      ellipsis: true,
      width: 160,
      sorter: (a, b) => a.lastName.localeCompare(b.lastName),
      render: value => <Markable tokens={debouncedSearch ?? ''}>{value}</Markable>,
    },
    {
      title: 'PLZ',
      dataIndex: 'zip',
      key: 'zip',
      ellipsis: true,
      width: 80,
      sorter: (a, b) => a.zip.localeCompare(b.zip),
      render: value => <Markable tokens={debouncedSearch ?? ''}>{value}</Markable>,
    },
    {
      title: 'Ort',
      dataIndex: 'city',
      key: 'city',
      ellipsis: true,
      width: 100,
      sorter: (a, b) => a.city.localeCompare(b.city),
      render: value => <Markable tokens={debouncedSearch ?? ''}>{value}</Markable>,
    },
  ];

  return (
    <MainContent>
      <PageHeader
        title="Zuweiserauswahl"
        extra={
          <Flex wrap gap={token.paddingXS} align="center">
            <Input
              autoFocus
              allowClear
              placeholder="Suche"
              value={search ?? ''}
              onChange={e => setSearch(e.target.value)}
              prefix={<SearchOutlined />}
              suffix={
                <Tooltip title="Suche nach Anzeigename, Kd.Nr., ME-Adresse, Ort, PLZ, Vorname, oder Nachname">
                  <InfoCircleOutlined />
                </Tooltip>
              }
              style={{ width: '300px' }}
            />
            <Popconfirm
              title="Achtung"
              classNames={{
                root: css`
                  max-width: 400px;
                `,
              }}
              description="Bitte vergewissern Sie sich, dass der gewünschte Zuweiser nicht schon angelegt wurde, um Duplikate zu vermeiden."
              okText="Verstanden, Zuweiser anlegen"
              placement="bottomRight"
              onConfirm={() => navigate('/zuweiserauswahl/create')}
            >
              <Button icon={<PlusCircleOutlined />} type="primary">
                Zuweiser anlegen
              </Button>
            </Popconfirm>
          </Flex>
        }
        style={{ padding: 0, paddingBottom: 'inherit' }}
      />
      <Table<Doctor>
        scroll={{ x: 'max-content' }}
        rowKey={record => record.id}
        sticky={true}
        size="middle"
        showSorterTooltip={false}
        dataSource={filteredDoctors}
        pagination={{
          size: 'middle',
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]} bis ${range[1]} von ${total} Zuweisern`,
        }}
        loading={loading}
        columns={columns}
        onRow={record => ({
          onClick: () => {
            setCurrentDoctorId(record.id);
            navigate('/pv-uebersicht');
          },
        })}
        rowClassName={tableHoverPointer}
      />
    </MainContent>
  );
};
